import { useState, useEffect, useCallback } from 'react';

/* ── COLOR GUIDE (dark bg: #0e0c0a to #0f172a) ────────────────
   #ffffff  Primary text — prices, headings, key values
   #ebebf5  Secondary headings
   #aeaeb2  Body text, descriptions, labels         ← use this+
   #8e8e93  Muted helper text, timestamps           ← minimum readable
   #636366  Very muted, truly secondary only
   NEVER use #334155 or #1e293b as text color — invisible
─────────────────────────────────────────────────────────────── */

const API_BASE = process.env.NODE_ENV === 'production'
  ? 'https://gammalens-api.onrender.com'
  : `http://${window.location.hostname}:8001`;

const HEATMAP_TICKERS = [
  // ETFs
  { ticker: 'SPY',  label: 'S&P 500 ETF',       sector: 'ETF' },
  { ticker: 'QQQ',  label: 'Nasdaq 100 ETF',     sector: 'ETF' },
  { ticker: 'IWM',  label: 'Russell 2000 ETF',   sector: 'ETF' },
  { ticker: 'DIA',  label: 'Dow Jones ETF',      sector: 'ETF' },
  { ticker: 'XLF',  label: 'Financials ETF',     sector: 'ETF' },
  { ticker: 'SMH',  label: 'Semiconductor ETF',  sector: 'ETF' },
  { ticker: 'GLD',  label: 'Gold ETF',           sector: 'ETF' },
  { ticker: 'SLV',  label: 'Silver ETF',         sector: 'ETF' },
  { ticker: 'XLE',  label: 'Energy ETF',         sector: 'ETF' },
  { ticker: 'XLK',  label: 'Tech ETF',           sector: 'ETF' },
  { ticker: 'XLV',  label: 'Healthcare ETF',     sector: 'ETF' },
  { ticker: 'ARKK', label: 'ARK Innovation ETF', sector: 'ETF' },
  // Semiconductors
  { ticker: 'NVDA', label: 'NVIDIA',             sector: 'Semi' },
  { ticker: 'AMD',  label: 'AMD',                sector: 'Semi' },
  { ticker: 'AVGO', label: 'Broadcom',           sector: 'Semi' },
  { ticker: 'SMCI', label: 'Super Micro',        sector: 'Semi' },
  { ticker: 'MU',   label: 'Micron',             sector: 'Semi' },
  { ticker: 'INTC', label: 'Intel',              sector: 'Semi' },
  { ticker: 'TSM',  label: 'TSMC',               sector: 'Semi' },
  { ticker: 'MRVL', label: 'Marvell',            sector: 'Semi' },
  { ticker: 'ARM',  label: 'ARM Holdings',       sector: 'Semi' },
  // Tech
  { ticker: 'AAPL', label: 'Apple',              sector: 'Tech' },
  { ticker: 'MSFT', label: 'Microsoft',          sector: 'Tech' },
  { ticker: 'GOOGL',label: 'Alphabet',           sector: 'Tech' },
  { ticker: 'AMZN', label: 'Amazon',             sector: 'Tech' },
  { ticker: 'META', label: 'Meta',               sector: 'Tech' },
  { ticker: 'NFLX', label: 'Netflix',            sector: 'Tech' },
  { ticker: 'CRM',  label: 'Salesforce',         sector: 'Tech' },
  { ticker: 'ORCL', label: 'Oracle',             sector: 'Tech' },
  { ticker: 'PLTR', label: 'Palantir',           sector: 'Tech' },
  { ticker: 'ADBE', label: 'Adobe',              sector: 'Tech' },
  { ticker: 'UBER', label: 'Uber',               sector: 'Tech' },
  { ticker: 'SHOP', label: 'Shopify',            sector: 'Tech' },
  { ticker: 'SNOW', label: 'Snowflake',          sector: 'Tech' },
  { ticker: 'NOW',  label: 'ServiceNow',         sector: 'Tech' },
  { ticker: 'DELL', label: 'Dell Technologies',  sector: 'Tech' },
  { ticker: 'BABA', label: 'Alibaba',            sector: 'Tech' },
  { ticker: 'RBLX', label: 'Roblox',             sector: 'Tech' },
  { ticker: 'IONQ', label: 'IonQ',               sector: 'Tech' },
  // Finance
  { ticker: 'JPM',  label: 'JPMorgan Chase',     sector: 'Finance' },
  { ticker: 'GS',   label: 'Goldman Sachs',      sector: 'Finance' },
  { ticker: 'BAC',  label: 'Bank of America',    sector: 'Finance' },
  { ticker: 'MS',   label: 'Morgan Stanley',     sector: 'Finance' },
  { ticker: 'C',    label: 'Citigroup',           sector: 'Finance' },
  { ticker: 'WFC',  label: 'Wells Fargo',         sector: 'Finance' },
  { ticker: 'V',    label: 'Visa',               sector: 'Finance' },
  { ticker: 'MA',   label: 'Mastercard',          sector: 'Finance' },
  // Consumer
  { ticker: 'TSLA', label: 'Tesla',              sector: 'Consumer' },
  { ticker: 'COST', label: 'Costco',             sector: 'Consumer' },
  { ticker: 'WMT',  label: 'Walmart',            sector: 'Consumer' },
  { ticker: 'HD',   label: 'Home Depot',          sector: 'Consumer' },
  { ticker: 'MCD',  label: "McDonald's",          sector: 'Consumer' },
  { ticker: 'NKE',  label: 'Nike',               sector: 'Consumer' },
  { ticker: 'SBUX', label: 'Starbucks',           sector: 'Consumer' },
  { ticker: 'TGT',  label: 'Target',              sector: 'Consumer' },
  { ticker: 'LOW',  label: "Lowe's",              sector: 'Consumer' },
  // Healthcare
  { ticker: 'UNH',  label: 'UnitedHealth',       sector: 'Healthcare' },
  { ticker: 'LLY',  label: 'Eli Lilly',          sector: 'Healthcare' },
  { ticker: 'JNJ',  label: 'Johnson & Johnson',   sector: 'Healthcare' },
  { ticker: 'PFE',  label: 'Pfizer',              sector: 'Healthcare' },
  { ticker: 'ABBV', label: 'AbbVie',              sector: 'Healthcare' },
  { ticker: 'MRK',  label: 'Merck',               sector: 'Healthcare' },
  { ticker: 'BMY',  label: 'Bristol-Myers',        sector: 'Healthcare' },
  { ticker: 'MRNA', label: 'Moderna',              sector: 'Healthcare' },
  // Energy
  { ticker: 'XOM',  label: 'Exxon Mobil',        sector: 'Energy' },
  { ticker: 'CVX',  label: 'Chevron',             sector: 'Energy' },
  { ticker: 'OXY',  label: 'Occidental',          sector: 'Energy' },
  { ticker: 'COP',  label: 'ConocoPhillips',      sector: 'Energy' },
  { ticker: 'DVN',  label: 'Devon Energy',         sector: 'Energy' },
  { ticker: 'ENPH', label: 'Enphase Energy',      sector: 'Energy' },
  // Crypto / Fintech
  { ticker: 'COIN', label: 'Coinbase',            sector: 'Crypto' },
  { ticker: 'MSTR', label: 'MicroStrategy',       sector: 'Crypto' },
  { ticker: 'IBIT', label: 'iShares Bitcoin Trust', sector: 'Crypto' },
  { ticker: 'SQ',   label: 'Block (Square)',       sector: 'Crypto' },
  { ticker: 'PYPL', label: 'PayPal',              sector: 'Crypto' },
  { ticker: 'SOFI', label: 'SoFi',               sector: 'Crypto' },
  { ticker: 'HOOD', label: 'Robinhood',           sector: 'Crypto' },
  { ticker: 'AFRM', label: 'Affirm',              sector: 'Crypto' },
  // Meme / High Volatility
  { ticker: 'GME',  label: 'GameStop',            sector: 'Meme' },
  { ticker: 'AMC',  label: 'AMC Entertainment',    sector: 'Meme' },
  { ticker: 'RIVN', label: 'Rivian',              sector: 'Meme' },
  { ticker: 'LCID', label: 'Lucid Motors',         sector: 'Meme' },
  { ticker: 'NIO',  label: 'NIO',                 sector: 'Meme' },
  { ticker: 'SNAP', label: 'Snap',               sector: 'Meme' },
  { ticker: 'ROKU', label: 'Roku',               sector: 'Meme' },
  { ticker: 'DKNG', label: 'DraftKings',          sector: 'Meme' },
  // Industrial / Defense
  { ticker: 'BA',   label: 'Boeing',              sector: 'Industrial' },
  { ticker: 'CAT',  label: 'Caterpillar',          sector: 'Industrial' },
  { ticker: 'LMT',  label: 'Lockheed Martin',      sector: 'Industrial' },
  { ticker: 'RTX',  label: 'Raytheon',             sector: 'Industrial' },
  { ticker: 'GE',   label: 'GE Aerospace',         sector: 'Industrial' },
  { ticker: 'DE',   label: 'Deere',                sector: 'Industrial' },
  // Media / Telecom
  { ticker: 'DIS',  label: 'Disney',              sector: 'Media' },
  { ticker: 'CMCSA',label: 'Comcast',             sector: 'Media' },
  { ticker: 'T',    label: 'AT&T',                sector: 'Media' },
  { ticker: 'TMUS', label: 'T-Mobile',            sector: 'Media' },
  // Other
  { ticker: 'ABNB', label: 'Airbnb',              sector: 'Other' },
  { ticker: 'PANW', label: 'Palo Alto Networks',   sector: 'Other' },
  { ticker: 'CRWD', label: 'CrowdStrike',          sector: 'Other' },
  { ticker: 'ZS',   label: 'Zscaler',              sector: 'Other' },
  { ticker: 'NET',  label: 'Cloudflare',           sector: 'Other' },
  // ── Expansion (mirrors backend/app/ticker_config.py TICKER_INFO) ──
  // Kept in sync with the backend so heatmap sector badges match the API
  // rather than falling into the "Other" catch-all. When adding new tickers
  // to the backend, add them here too.
  // ETF — sectoral + fixed income + macro
  { ticker: 'XLI',  label: 'Industrials ETF',           sector: 'ETF' },
  { ticker: 'XLY',  label: 'Consumer Discretionary ETF', sector: 'ETF' },
  { ticker: 'XLP',  label: 'Consumer Staples ETF',      sector: 'ETF' },
  { ticker: 'XLU',  label: 'Utilities ETF',             sector: 'ETF' },
  { ticker: 'XLRE', label: 'Real Estate ETF',           sector: 'ETF' },
  { ticker: 'XLB',  label: 'Materials ETF',             sector: 'ETF' },
  { ticker: 'XLC',  label: 'Communication Services ETF', sector: 'ETF' },
  { ticker: 'TLT',  label: '20+ Year Treasuries ETF',   sector: 'ETF' },
  { ticker: 'IEF',  label: '7-10Y Treasury ETF',        sector: 'ETF' },
  { ticker: 'HYG',  label: 'High Yield Bond ETF',       sector: 'ETF' },
  { ticker: 'KRE',  label: 'Regional Banks ETF',        sector: 'ETF' },
  { ticker: 'XBI',  label: 'Biotech ETF',               sector: 'ETF' },
  { ticker: 'USO',  label: 'Oil Fund ETF',              sector: 'ETF' },
  { ticker: 'UUP',  label: 'Dollar Index ETF',          sector: 'ETF' },
  // Semiconductors — analog + equipment
  { ticker: 'QCOM', label: 'Qualcomm',                  sector: 'Semi' },
  { ticker: 'ASML', label: 'ASML',                      sector: 'Semi' },
  { ticker: 'AMAT', label: 'Applied Materials',         sector: 'Semi' },
  { ticker: 'LRCX', label: 'Lam Research',              sector: 'Semi' },
  { ticker: 'KLAC', label: 'KLA',                       sector: 'Semi' },
  { ticker: 'TXN',  label: 'Texas Instruments',         sector: 'Semi' },
  { ticker: 'ADI',  label: 'Analog Devices',            sector: 'Semi' },
  { ticker: 'NXPI', label: 'NXP Semiconductors',        sector: 'Semi' },
  { ticker: 'ON',   label: 'ON Semiconductor',          sector: 'Semi' },
  { ticker: 'MPWR', label: 'Monolithic Power',          sector: 'Semi' },
  // Tech — software + China ADRs + adtech + Reddit
  { ticker: 'DDOG', label: 'Datadog',                   sector: 'Tech' },
  { ticker: 'MDB',  label: 'MongoDB',                   sector: 'Tech' },
  { ticker: 'TEAM', label: 'Atlassian',                 sector: 'Tech' },
  { ticker: 'WDAY', label: 'Workday',                   sector: 'Tech' },
  { ticker: 'S',    label: 'SentinelOne',               sector: 'Tech' },
  { ticker: 'JD',   label: 'JD.com',                    sector: 'Tech' },
  { ticker: 'PDD',  label: 'PDD Holdings',              sector: 'Tech' },
  { ticker: 'APP',  label: 'AppLovin',                  sector: 'Tech' },
  { ticker: 'RDDT', label: 'Reddit',                    sector: 'Tech' },
  // Finance — insurance + brokers + Berkshire
  { ticker: 'BRKB', label: 'Berkshire Hathaway B',      sector: 'Finance' },
  { ticker: 'AIG',  label: 'AIG',                       sector: 'Finance' },
  { ticker: 'PGR',  label: 'Progressive',               sector: 'Finance' },
  { ticker: 'SCHW', label: 'Charles Schwab',            sector: 'Finance' },
  { ticker: 'IBKR', label: 'Interactive Brokers',       sector: 'Finance' },
  // Consumer — homebuilders + autos
  { ticker: 'LEN',  label: 'Lennar',                    sector: 'Consumer' },
  { ticker: 'DHI',  label: 'D.R. Horton',               sector: 'Consumer' },
  { ticker: 'TOL',  label: 'Toll Brothers',             sector: 'Consumer' },
  { ticker: 'F',    label: 'Ford Motor',                sector: 'Consumer' },
  { ticker: 'GM',   label: 'General Motors',            sector: 'Consumer' },
  // Staples
  { ticker: 'PG',   label: 'Procter & Gamble',          sector: 'Staples' },
  { ticker: 'KO',   label: 'Coca-Cola',                 sector: 'Staples' },
  { ticker: 'PEP',  label: 'PepsiCo',                   sector: 'Staples' },
  { ticker: 'CL',   label: 'Colgate-Palmolive',         sector: 'Staples' },
  { ticker: 'MO',   label: 'Altria',                    sector: 'Staples' },
  { ticker: 'KHC',  label: 'Kraft Heinz',               sector: 'Staples' },
  // REIT
  { ticker: 'PLD',  label: 'Prologis',                  sector: 'REIT' },
  { ticker: 'AMT',  label: 'American Tower',            sector: 'REIT' },
  { ticker: 'EQIX', label: 'Equinix',                   sector: 'REIT' },
  // Utilities
  { ticker: 'NEE',  label: 'NextEra Energy',            sector: 'Utilities' },
  { ticker: 'DUK',  label: 'Duke Energy',               sector: 'Utilities' },
  { ticker: 'SO',   label: 'Southern Company',          sector: 'Utilities' },
  // Healthcare — biotech + medical devices
  { ticker: 'AMGN', label: 'Amgen',                     sector: 'Healthcare' },
  { ticker: 'GILD', label: 'Gilead Sciences',           sector: 'Healthcare' },
  { ticker: 'REGN', label: 'Regeneron',                 sector: 'Healthcare' },
  { ticker: 'VRTX', label: 'Vertex Pharmaceuticals',    sector: 'Healthcare' },
  { ticker: 'ISRG', label: 'Intuitive Surgical',        sector: 'Healthcare' },
  { ticker: 'MDT',  label: 'Medtronic',                 sector: 'Healthcare' },
  // Crypto — spot ETFs + miners
  { ticker: 'FBTC', label: 'Fidelity Bitcoin ETF',      sector: 'Crypto' },
  { ticker: 'ETHA', label: 'iShares Ethereum ETF',      sector: 'Crypto' },
  { ticker: 'BITO', label: 'ProShares Bitcoin ETF',     sector: 'Crypto' },
  { ticker: 'MARA', label: 'Marathon Digital',          sector: 'Crypto' },
  { ticker: 'RIOT', label: 'Riot Platforms',            sector: 'Crypto' },
  { ticker: 'CLSK', label: 'CleanSpark',                sector: 'Crypto' },
  // Meme — SoundHound + Carvana + Upstart
  { ticker: 'SOUN', label: 'SoundHound AI',             sector: 'Meme' },
  { ticker: 'CVNA', label: 'Carvana',                   sector: 'Meme' },
  { ticker: 'UPST', label: 'Upstart',                   sector: 'Meme' },
  // Industrial — airlines + cruise + space
  { ticker: 'DAL',  label: 'Delta Air Lines',           sector: 'Industrial' },
  { ticker: 'UAL',  label: 'United Airlines',           sector: 'Industrial' },
  { ticker: 'CCL',  label: 'Carnival',                  sector: 'Industrial' },
  { ticker: 'RKLB', label: 'Rocket Lab',                sector: 'Industrial' },
  { ticker: 'ASTS', label: 'AST SpaceMobile',           sector: 'Industrial' },
  // Energy — small modular nuclear
  { ticker: 'SMR',  label: 'NuScale Power',             sector: 'Energy' },
];

// Snapshot cards show only Tier 1+2 (50 tickers) — warm from morning fetch
// Full HEATMAP_TICKERS (100) kept for future table/filter view
const SNAPSHOT_TICKERS = HEATMAP_TICKERS.filter(t =>
  ['SPY','QQQ','IWM','NVDA','TSLA','AAPL','MSFT','AMZN','META','AMD',
   'GOOGL','NFLX','PLTR','SMH','DIA','XLF','GLD','SLV','AVGO','SMCI',
   'MU','CRM','ORCL','JPM','GS','BAC','COST','WMT','UNH','LLY','XOM',
   'INTC','TSM','ADBE','UBER','COIN','MSTR','MS','V','MA','HD','JNJ',
   'PFE','BA','ARKK','XLE','XLK','GME','SOFI','PANW'
  ].includes(t.ticker)
);

const REGIME_CONFIG = {
  'POSITIVE GAMMA': { color: '#30d158', bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.2)',  label: 'POS GAMMA', short: '+GEX' },
  'NEGATIVE GAMMA': { color: '#ff453a', bg: 'rgba(239,68,68,0.08)',  border: 'rgba(239,68,68,0.2)',   label: 'NEG GAMMA', short: '-GEX' },
  'NEAR FLIP':      { color: '#ff9f0a', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.2)',  label: 'NEAR FLIP', short: 'FLIP' },
};

function formatGex(n) {
  if (n == null) return '—';
  const sign = n >= 0 ? '+' : '';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${sign}${(n / 1e9).toFixed(2)}B`;
  if (abs >= 100e6) return `${sign}${(n / 1e6).toFixed(0)}M`;   // 100M+ → no decimal
  if (abs >= 1e6) return `${sign}${(n / 1e6).toFixed(1)}M`;     // 1-99M → 1 decimal
  if (abs >= 1e3) return `${sign}${(n / 1e3).toFixed(0)}K`;
  return `${sign}${n.toFixed(0)}`;
}

function getMktStatus() {
  const now = new Date();
  const et = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const day = et.getDay(); // 0=Sun, 6=Sat
  if (day === 0 || day === 6) return 'closed';
  // NYSE holidays 2025–2026
  const holidays = [
    '2025-01-01','2025-01-20','2025-02-17','2025-04-18','2025-05-26',
    '2025-06-19','2025-07-04','2025-09-01','2025-11-27','2025-12-25',
    '2026-01-01','2026-01-19','2026-02-16','2026-04-03','2026-05-25',
    '2026-06-19','2026-07-03','2026-09-07','2026-11-26','2026-12-25',
  ];
  const dateStr = et.getFullYear() + '-' +
    String(et.getMonth() + 1).padStart(2, '0') + '-' +
    String(et.getDate()).padStart(2, '0');
  if (holidays.includes(dateStr)) return 'closed';
  const mins = et.getHours() * 60 + et.getMinutes();
  if (mins >= 570 && mins < 960) return 'open';   // 9:30–16:00 ET
  if (mins >= 480 && mins < 570) return 'pre';    // 8:00–9:30 ET
  return 'closed';
}

function getRegimeKey(levels) {
  if (!levels) return null;
  const pos = levels.dealer_positioning || '';
  if (pos.includes('NEGATIVE')) return 'NEGATIVE GAMMA';
  return 'POSITIVE GAMMA';
}

function isNearFlip(levels) {
  if (!levels || !levels.spot_price || !levels.gamma_flip) return false;
  return Math.abs(levels.spot_price - levels.gamma_flip) / levels.spot_price < 0.01;
}

function TickerCard({ ticker, label, data, loading, onSelect, isSelected }) {
  const [fallbackData, setFallbackData] = useState(null);
  const [fallbackLoading, setFallbackLoading] = useState(false);
  const [fallbackAttempted, setFallbackAttempted] = useState(false);

  useEffect(() => {
    if (loading || data || fallbackAttempted) return;
    setFallbackAttempted(true);
    setFallbackLoading(true);

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);

    fetch(`${API_BASE}/api/gex/${ticker}`, { signal: controller.signal })
      .then(r => r.json())
      .then(d => {
        clearTimeout(timeout);
        if (d?.key_levels) setFallbackData(d.key_levels);
        else if (d?.analysis?.key_levels) setFallbackData(d.analysis.key_levels);
        setFallbackLoading(false);
      })
      .catch(() => {
        clearTimeout(timeout);
        setFallbackLoading(false);
      });

    return () => { clearTimeout(timeout); controller.abort(); };
  }, [ticker, loading, data, fallbackAttempted]);

  const effectiveData = data || fallbackData;
  const effectiveLoading = loading || fallbackLoading;
  const error = !effectiveLoading && !effectiveData;
  const regimeKey = getRegimeKey(effectiveData);
  const cfg       = REGIME_CONFIG[regimeKey] || REGIME_CONFIG['POSITIVE GAMMA'];
  const spot      = effectiveData?.spot_price;
  const cw        = effectiveData?.call_wall;
  const pw        = effectiveData?.put_wall;
  const flip      = effectiveData?.gamma_flip;

  const flipDist = (spot && flip)
    ? Math.abs(spot - flip) / spot * 100
    : null;
  const nearFlip = flipDist !== null && flipDist < 1.0;

  return (
    <div
      onClick={() => !effectiveLoading && onSelect(ticker)}
      style={{
        background: isSelected
          ? `linear-gradient(145deg, ${cfg.bg}, rgba(255,255,255,0.03))`
          : `linear-gradient(145deg, #1a1a1f, #111114)`,
        border: isSelected
          ? `1px solid ${cfg.color}50`
          : 'rgba(255,255,255,0.06)',
        borderTop: isSelected
          ? `2px solid ${cfg.color}`
          : '2px solid rgba(255,255,255,0.08)',
        borderRadius: '14px',
        padding: '16px',
        cursor: 'pointer',
        transition: 'all 0.2s cubic-bezier(0.16,1,0.3,1)',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: isSelected ? `0 8px 32px ${cfg.color}18` : 'none',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = `0 12px 40px ${cfg.color}20`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.boxShadow = isSelected ? `0 8px 32px ${cfg.color}18` : 'none';
      }}
    >
      {/* Near flip warning pulse */}
      {nearFlip && !effectiveLoading && (
        <div style={{
          position: 'absolute', top: 8, right: 8,
          width: 6, height: 6, borderRadius: '50%',
          background: '#ff9f0a',
          boxShadow: '0 0 8px rgba(245,158,11,0.8)',
          animation: 'hm-pulse 1.5s ease infinite',
        }} />
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
        <div>
          <div style={{
            fontSize: '16px', fontWeight: 900,
            color: '#ffffff', letterSpacing: '-0.3px',
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            {ticker}
          </div>
          <div style={{ fontSize: '9px', color: '#8e8e93', marginTop: '1px' }}>
            {label}
          </div>
        </div>
        {!effectiveLoading && !error && (
          <div style={{
            fontSize: '7px', fontWeight: 800,
            color: cfg.color, letterSpacing: '1px',
            background: `${cfg.color}12`,
            border: `1px solid ${cfg.color}25`,
            padding: '3px 7px', borderRadius: '5px',
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            {cfg.short}
          </div>
        )}
      </div>

      {effectiveLoading ? (
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <div style={{
            width: 16, height: 16,
            border: '2px solid rgba(255,255,255,0.03)',
            borderTop: `2px solid ${cfg.color}`,
            borderRadius: '50%',
            animation: 'hm-spin 0.8s linear infinite',
            margin: '0 auto 8px',
          }} />
          <div style={{ fontSize: '9px', color: '#8e8e93' }}>Fetching...</div>
        </div>
      ) : error ? (
        <div style={{ padding: '4px 0' }}>
          <div style={{
            fontSize: '22px', fontWeight: 900,
            color: 'rgba(255,255,255,0.10)',
            letterSpacing: '-1px', lineHeight: 1,
            marginBottom: '16px',
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            $———
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[
              { l: 'CALL WALL', c: '#30d158' },
              { l: 'GAMMA FLIP', c: '#bf5af2' },
              { l: 'PUT WALL', c: '#ff453a' },
            ].map(item => (
              <div key={item.l} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '8px', color: `${item.c}40`, fontFamily: 'monospace', letterSpacing: '0.5px' }}>{item.l}</span>
                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.08)', fontFamily: 'monospace' }}>———</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* Spot price */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{
              fontSize: '22px', fontWeight: 900,
              color: '#ff9f0a', letterSpacing: '-1px',
              lineHeight: 1,
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              ${spot?.toFixed(2)}
            </div>
            {effectiveData?.fromCache && (
              <div style={{ fontSize: '8px', color: '#8e8e93', marginTop: '2px' }}>
                last session
              </div>
            )}
          </div>

          {/* Key levels */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            {[
              { label: 'CW',   value: cw,   color: '#30d158' },
              { label: 'FLIP', value: flip, color: '#bf5af2' },
              { label: 'PW',   value: pw,   color: '#ff453a' },
            ].map(lv => (
              <div key={lv.label} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{
                  fontSize: '8px', fontWeight: 700, color: lv.color,
                  letterSpacing: '0.8px', opacity: 0.7,
                  fontFamily: "'JetBrains Mono', monospace",
                }}>
                  {lv.label}
                </span>
                <span style={{
                  fontSize: '12px', fontWeight: 700,
                  color: '#aeaeb2',
                  fontFamily: "'JetBrains Mono', monospace",
                }}>
                  ${lv.value?.toFixed(0) ?? '—'}
                </span>
              </div>
            ))}
          </div>

          {/* Flip distance bar */}
          {flipDist !== null && (
            <div style={{ marginTop: '10px' }}>
              <div style={{
                height: '2px',
                background: 'rgba(255,255,255,0.03)',
                borderRadius: '1px',
                overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%',
                  width: `${Math.min(100 - flipDist * 20, 100)}%`,
                  background: nearFlip
                    ? 'linear-gradient(90deg, #ff9f0a, #ff9f0a)'
                    : `linear-gradient(90deg, ${cfg.color}60, ${cfg.color})`,
                  borderRadius: '1px',
                  transition: 'width 0.8s ease',
                }} />
              </div>
              <div style={{ fontSize: '8px', color: '#8e8e93', marginTop: '3px' }}>
                {flipDist.toFixed(1)}% from flip
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function GammaSummaryTable({ rows, loading, onSelect, initialFilter, onFilterChange }) {
  const [gammaFilter, setGammaFilter] = useState(initialFilter || 'all');
  const handleFilter = (f) => { setGammaFilter(f); onFilterChange?.(f); };

  // Group by sector — use all 100 tickers
  const sectors = {};
  HEATMAP_TICKERS.forEach(t => {
    if (!sectors[t.sector]) sectors[t.sector] = [];
    sectors[t.sector].push(t);
  });
  // Backend serves ~175 tickers via /api/gex/summary but this HEATMAP_TICKERS
  // list is a hand-curated ~100. Any backend ticker not in the hardcoded list
  // was silently dropped by every filter (visible symptom: "N diverging" chip
  // count > rendered row count). Sweep rows for orphans and park them in an
  // "Other" pseudo-sector so the count is honest across all filter views.
  const known = new Set(HEATMAP_TICKERS.map(t => t.ticker));
  const orphans = Object.keys(rows || {}).filter(k => !known.has(k));
  if (orphans.length > 0) {
    if (!sectors['Other']) sectors['Other'] = [];
    orphans.forEach(sym => sectors['Other'].push({ ticker: sym, label: '', sector: 'Other' }));
    sectors['Other'].sort((a, b) => a.ticker.localeCompare(b.ticker));
  }

  return (
    <div>
      {/* Gamma filter */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '14px', flexWrap: 'wrap' }}>
        {[
          { id: 'all', label: 'All', color: '#8e8e93' },
          { id: 'POSITIVE GAMMA', label: '+GEX', color: '#30d158' },
          { id: 'NEGATIVE GAMMA', label: '-GEX', color: '#ff453a' },
          { id: 'NEAR FLIP', label: 'Near Flip', color: '#ff9f0a' },
          { id: 'FLIPPED', label: 'Flipped', color: '#bf5af2' },
          { id: 'EXCEPTIONS', label: 'Diverging', color: '#fbbf24' },
          ...(gammaFilter === 'SECTOR' ? [{ id: 'SECTOR', label: sessionStorage.getItem('gl_sector') || 'Sector', color: '#60a5fa' }] : []),
        ].map(f => (
          <button key={f.id} onClick={() => handleFilter(f.id)} style={{
            padding: '4px 12px', borderRadius: '6px', border: 'none',
            background: gammaFilter === f.id ? `${f.color}18` : 'rgba(255,255,255,0.03)',
            color: gammaFilter === f.id ? f.color : '#636366',
            fontSize: '10px', fontWeight: 700, cursor: 'pointer',
            fontFamily: "'JetBrains Mono', monospace",
            borderWidth: '1px', borderStyle: 'solid',
            borderColor: gammaFilter === f.id ? `${f.color}35` : 'rgba(255,255,255,0.06)',
            transition: 'all 0.15s',
          }}>{f.label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <div style={{
            width: 20, height: 20, margin: '0 auto 12px',
            border: '2px solid rgba(255,255,255,0.06)',
            borderTop: '2px solid #30d158',
            borderRadius: '50%', animation: 'hm-spin 0.8s linear infinite',
          }} />
          <div style={{ fontSize: '11px', color: '#8e8e93' }}>Loading all tickers...</div>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="hm-table" style={{
            width: '100%', borderCollapse: 'collapse',
            fontFamily: "'JetBrains Mono', monospace", fontSize: '12px',
          }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                {[
                  { label: 'Ticker', hide: false },
                  { label: 'Spot', hide: false },
                  { label: 'Gamma', hide: false },
                  { label: 'Call Wall', hide: true },
                  { label: 'Put Wall', hide: true },
                  { label: 'Flip', hide: true },
                  { label: 'Flip Dist', hide: true },
                  { label: 'Net GEX', hide: false },
                ].map(h => (
                  <th key={h.label} className={h.hide ? 'hm-col-hide' : ''} style={{
                    padding: '8px 10px', textAlign: h.label === 'Ticker' ? 'left' : 'right',
                    fontSize: '9px', color: '#636366', fontWeight: 700,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                  }}>{h.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(sectors).map(([sector, tickers]) => {
                const sessionTickers = (gammaFilter === 'FLIPPED' || gammaFilter === 'EXCEPTIONS')
                  ? (() => { try { return JSON.parse(sessionStorage.getItem(gammaFilter === 'FLIPPED' ? 'gl_flipped' : 'gl_exceptions') || '[]'); } catch { return []; } })()
                  : [];
                const sectorFilter = gammaFilter === 'SECTOR'
                  ? (sessionStorage.getItem('gl_sector') || '')
                  : '';
                const filtered = gammaFilter === 'all'
                  ? tickers
                  : gammaFilter === 'SECTOR'
                  ? tickers.filter(t => t.sector === sectorFilter)
                  : (gammaFilter === 'FLIPPED' || gammaFilter === 'EXCEPTIONS')
                  ? tickers.filter(t => sessionTickers.includes(t.ticker))
                  : gammaFilter === 'NEAR FLIP'
                  ? tickers.filter(t => isNearFlip(rows[t.ticker]))
                  : tickers.filter(t => getRegimeKey(rows[t.ticker]) === gammaFilter);
                return filtered.map((t, i) => {
                  const d = rows[t.ticker];
                  const regimeKey = getRegimeKey(d);
                  const cfg = REGIME_CONFIG[regimeKey] || REGIME_CONFIG['POSITIVE GAMMA'];
                  const spot = d?.spot_price;
                  const flip = d?.gamma_flip;
                  const flipDist = (spot && flip) ? ((flip - spot) / spot * 100) : null;
                  const netGex = d?.net_gex;

                  return (
                    <tr
                      key={t.ticker}
                      onClick={() => onSelect(t.ticker)}
                      style={{
                        cursor: 'pointer',
                        borderBottom: '1px solid rgba(255,255,255,0.04)',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>
                        <span style={{ fontWeight: 800, color: '#fff', fontSize: '12px' }}>{t.ticker}</span>
                        <span className="hm-ticker-name" style={{ color: '#636366', fontSize: '9px', marginLeft: '6px' }}>{t.label}</span>
                        {i === 0 && (
                          <span className="hm-sector-badge" style={{
                            marginLeft: '8px', fontSize: '8px', color: '#8e8e93',
                            background: 'rgba(255,255,255,0.04)', padding: '1px 5px',
                            borderRadius: '3px', letterSpacing: '0.05em',
                          }}>{sector}</span>
                        )}
                      </td>
                      <td style={{ padding: '7px 10px', textAlign: 'right', color: '#aeaeb2', fontWeight: 600 }}>
                        {spot ? `$${spot.toFixed(2)}` : '--'}
                      </td>
                      <td style={{ padding: '7px 10px', textAlign: 'right' }}>
                        {d ? (
                          <span style={{
                            fontSize: '9px', fontWeight: 800, color: cfg.color,
                            background: `${cfg.color}12`, border: `1px solid ${cfg.color}25`,
                            padding: '2px 6px', borderRadius: '4px', letterSpacing: '0.5px',
                          }}>{cfg.short}</span>
                        ) : '--'}
                      </td>
                      <td className="hm-col-hide" style={{ padding: '7px 10px', textAlign: 'right', color: '#30d158', fontWeight: 600 }}>
                        {d?.call_wall ? `$${d.call_wall.toFixed(0)}` : '--'}
                      </td>
                      <td className="hm-col-hide" style={{ padding: '7px 10px', textAlign: 'right', color: '#ff453a', fontWeight: 600 }}>
                        {d?.put_wall ? `$${d.put_wall.toFixed(0)}` : '--'}
                      </td>
                      <td className="hm-col-hide" style={{ padding: '7px 10px', textAlign: 'right', color: '#bf5af2', fontWeight: 600 }}>
                        {flip ? `$${flip.toFixed(0)}` : '--'}
                      </td>
                      <td className="hm-col-hide" style={{ padding: '7px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {flipDist !== null ? (
                          <span style={{
                            color: Math.abs(flipDist) < 1 ? '#ff9f0a' : '#aeaeb2',
                            fontWeight: Math.abs(flipDist) < 1 ? 700 : 500,
                          }}>
                            {flipDist > 0 ? '+' : ''}{flipDist.toFixed(1)}%
                          </span>
                        ) : '--'}
                      </td>
                      <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 700, color: netGex >= 0 ? '#30d158' : '#ff453a' }}>
                        {netGex != null ? formatGex(netGex) : '--'}
                      </td>
                    </tr>
                  );
                });
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function GEXHeatmap({ onSelectTicker, currentTicker, initialFilter, onBack }) {
  const [mktStatus, setMktStatus] = useState(getMktStatus);
  const [refreshKey, setRefreshKey] = useState(0);
  const viewMode = 'summary';
  const [activeFilter, setActiveFilter] = useState(initialFilter || 'all');

  // Shared summary data — one fetch powers both Summary and Cards views
  const [summaryData, setSummaryData] = useState({});
  const [summaryLoading, setSummaryLoading] = useState(true);

  useEffect(() => {
    setSummaryLoading(true);
    let cancelled = false;
    fetch(`${API_BASE}/api/gex/summary`)
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        const results = {};
        if (data?.tickers) {
          Object.entries(data.tickers).forEach(([ticker, d]) => {
            if (d?.key_levels) results[ticker] = d.key_levels;
          });
        }
        setSummaryData(results);
        setSummaryLoading(false);
      })
      .catch(() => { if (!cancelled) setSummaryLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey]);

  // Poll market status every 30s; auto-refresh cards when market opens
  useEffect(() => {
    let prevStatus = getMktStatus();

    const tick = setInterval(() => {
      const now = getMktStatus();
      setMktStatus(now);

      // Trigger immediate refresh the moment market transitions to open
      if (prevStatus !== 'open' && now === 'open') {
        setRefreshKey(k => k + 1);
      }
      prevStatus = now;
    }, 30_000);

    return () => clearInterval(tick);
  }, []);

  // Refresh every 5 minutes while market is open
  useEffect(() => {
    if (mktStatus !== 'open') return;
    const interval = setInterval(() => {
      setRefreshKey(k => k + 1);
    }, 15 * 60_000);
    return () => clearInterval(interval);
  }, [mktStatus]);

  const handleSelect = useCallback((ticker) => {
    if (onSelectTicker) onSelectTicker(ticker);
  }, [onSelectTicker]);

  return (
    <div>
      <style>{`
        @keyframes hm-spin { to { transform: rotate(360deg) } }
        @keyframes hm-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.4;transform:scale(0.7)} }
        @keyframes hm-ring { 0%{transform:scale(1);opacity:0.6} 100%{transform:scale(2.4);opacity:0} }
        @media (max-width: 768px) {
          .hm-desk-only { display: none !important; }
          .hm-header { flex-direction: column !important; align-items: flex-start !important; gap: 8px !important; }
          .hm-header-right { width: 100% !important; justify-content: space-between !important; }
          .hm-view-toggle { display: none !important; }
          .hm-mkt-badge { display: none !important; }
          .hm-col-hide { display: none !important; }
          .hm-ticker-name { display: none !important; }
          .hm-sector-badge { display: none !important; }
          .hm-table td, .hm-table th { padding: 6px 6px !important; font-size: 11px !important; }
        }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        {onBack && (
          <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', transition: 'all 0.15s', flexShrink: 0, padding: 0 }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '15px', fontWeight: 700, color: '#ffffff', letterSpacing: '-0.4px', fontFamily: 'Inter, sans-serif' }}>
            {activeFilter === 'SECTOR' ? `${sessionStorage.getItem('gl_sector') || 'Sector'} GEX` :
             activeFilter === 'POSITIVE GAMMA' ? 'Positive Gamma Tickers' :
             activeFilter === 'NEGATIVE GAMMA' ? 'Negative Gamma Tickers' :
             activeFilter === 'NEAR FLIP' ? 'Near Flip Tickers' :
             activeFilter === 'FLIPPED' ? 'Regime Flips Today' :
             activeFilter === 'EXCEPTIONS' ? 'Diverging Tickers' :
             'GEX Snapshot'}
          </div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginTop: '2px', fontFamily: 'Inter, sans-serif' }}>
            {activeFilter === 'SECTOR' ? `Gamma positioning for ${sessionStorage.getItem('gl_sector') || 'sector'} tickers` :
             activeFilter === 'POSITIVE GAMMA' ? 'Dealers suppressing moves on these names' :
             activeFilter === 'NEGATIVE GAMMA' ? 'Dealers amplifying moves on these names' :
             activeFilter === 'NEAR FLIP' ? 'Tickers close to flipping gamma regime' :
             activeFilter === 'FLIPPED' ? 'Tickers that changed regime since open' :
             activeFilter === 'EXCEPTIONS' ? 'Tickers diverging from the dominant regime' :
             'Dealer gamma exposure across the market'}
          </div>
        </div>
        {/* Color legend — desktop only */}
        <div className="hm-desk-only" style={{ display: 'flex', gap: '14px', flexShrink: 0 }}>
          {[
            { color: '#30d158', label: '+GEX' },
            { color: '#ff453a', label: '-GEX' },
            { color: '#ff9f0a', label: 'Near Flip' },
          ].map(l => (
            <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: 6, height: 6, borderRadius: 2, background: l.color }} />
              <span style={{ fontSize: '9px', color: '#8e8e93' }}>{l.label}</span>
            </div>
          ))}
        </div>
      </div>

      {viewMode === 'summary' ? (
        <GammaSummaryTable rows={summaryData} loading={summaryLoading} onSelect={handleSelect} initialFilter={initialFilter} onFilterChange={setActiveFilter} />
      ) : (
        <>
          {/* Cards grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: '12px',
          }}>
            {SNAPSHOT_TICKERS.map(({ ticker, label }) => (
              <TickerCard
                key={ticker}
                ticker={ticker}
                label={label}
                data={summaryData[ticker]}
                loading={summaryLoading}
                onSelect={handleSelect}
                isSelected={currentTicker === ticker}
              />
            ))}
          </div>

          {/* Footer hint */}
          <div style={{
            marginTop: '16px', textAlign: 'center',
            fontSize: '10px', color: '#636366',
          }}>
            Click any ticker to load its GEX analysis
          </div>
        </>
      )}
    </div>
  );
}
